import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Shield, ExternalLink, Key, Unplug, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

interface SecurityStatus {
  connected: boolean;
  summary: {
    posture_score: number | null;
    open_findings: number;
    critical_vulns: number;
    last_scan_at: string | null;
  } | null;
}

function ScoreRing({ score }: { score: number }) {
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center justify-center">
      <div
        className="w-24 h-24 rounded-full flex items-center justify-center border-4"
        style={{ borderColor: color }}
      >
        <span className="text-2xl font-bold" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

export function SecurityPage() {
  const qc = useQueryClient();
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [connectError, setConnectError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['security-status'],
    queryFn: () => api<SecurityStatus>('security/integration/status'),
  });

  const connectMutation = useMutation({
    mutationFn: (api_key: string) =>
      api<{ connected: boolean }>('security/integration/connect', { method: 'POST', body: { api_key } }),
    onSuccess: () => {
      setApiKeyInput('');
      setConnectError('');
      qc.invalidateQueries({ queryKey: ['security-status'] });
    },
    onError: (err) => {
      setConnectError(err instanceof ApiError ? err.message : 'Failed to connect');
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api('security/integration/disconnect', { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['security-status'] }),
  });

  const launchMutation = useMutation({
    mutationFn: () => api<{ redirect_url: string }>('security/integration/launch', { method: 'POST' }),
    onSuccess: ({ redirect_url }) => window.open(redirect_url, '_blank'),
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const connected = data?.connected ?? false;
  const summary = data?.summary ?? null;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
            <Shield className="h-5 w-5 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">The One Security</h1>
            <p className="text-sm text-muted-foreground">AI-powered vulnerability scanning &amp; pentesting</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {connected && (
            <button
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground hover:text-destructive border border-border rounded-md hover:border-destructive transition-colors"
            >
              <Unplug className="h-3.5 w-3.5" />
              Disconnect
            </button>
          )}
          <button
            onClick={() => launchMutation.mutate()}
            disabled={launchMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
          >
            {launchMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ExternalLink className="h-3.5 w-3.5" />
            )}
            Launch The One Security
          </button>
        </div>
      </div>

      {/* Connected — show summary */}
      {connected && summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="md:col-span-1 rounded-lg border border-border bg-card p-6 flex flex-col items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Posture Score</p>
            {summary.posture_score != null ? (
              <ScoreRing score={summary.posture_score} />
            ) : (
              <span className="text-2xl font-bold text-muted-foreground">—</span>
            )}
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Open Findings</p>
            <p className="text-3xl font-bold text-foreground">{summary.open_findings}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Critical Vulns</p>
            <p className="text-3xl font-bold text-destructive">{summary.critical_vulns}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Last Scan</p>
            <p className="text-sm font-medium text-foreground">
              {summary.last_scan_at
                ? new Date(summary.last_scan_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : 'No scans yet'}
            </p>
          </div>
        </div>
      )}

      {connected && !summary && (
        <div className="mb-8 flex items-center gap-2 p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          Connected but could not load live data. Your API key may have insufficient permissions.
        </div>
      )}

      {/* Connect section */}
      {!connected && (
        <div className="rounded-lg border border-border bg-card p-8">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
              <Key className="h-5 w-5 text-indigo-500" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-foreground mb-1">Connect your account</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Paste an API key from The One Security to link your account and see live security data here.
                Generate keys in{' '}
                <a
                  href="https://app.theonesecurity.app/settings"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline underline-offset-2"
                >
                  Settings → API Keys
                </a>
                .
              </p>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="onesec_..."
                  className="flex-1 border border-border rounded-md px-3 py-2 text-sm bg-background font-mono"
                />
                <button
                  onClick={() => connectMutation.mutate(apiKeyInput)}
                  disabled={connectMutation.isPending || apiKeyInput.length < 10}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {connectMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Connect
                </button>
              </div>
              {connectError && (
                <p className="mt-2 text-sm text-destructive">{connectError}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feature highlights */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { title: 'CVE Feed', desc: 'NVD, OSV, GHSA & KEV — updated daily, cross-referenced against your stack.' },
          { title: 'AI Pentest Agents', desc: '5 agent types covering auth, isolation, API fuzzing, business logic & infra.' },
          { title: 'Compliance Posture', desc: 'Continuous scoring against SOC 2, ISO 27001, NIST & custom frameworks.' },
        ].map((f) => (
          <div key={f.title} className="rounded-lg border border-border bg-card p-5">
            <h3 className="font-semibold text-sm text-foreground mb-1">{f.title}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
